import "./Courier.css";
import { useState, useEffect, createContext, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { CourierDashboard } from "./pages/CourierDashboard";
import { ActiveRoute } from "./pages/ActiveRoute";
import { PackageList } from "./pages/PackageList";
import { PackageDetail } from "./pages/PackageDetail";
import { CourierPerformance } from "./pages/CourierPerformance";
import { CourierProfile } from "./pages/CourierProfile";
import { CourierNotifications } from "./pages/CourierNotifications";
import { CourierPrivacySecurity } from "./pages/CourierPrivacySecurity";
import { CourierHelp } from "./pages/CourierHelp";
import { CourierMessages } from "./pages/CourierMessages";
import { CourierLanguageProvider } from "./contexts/CourierLanguageContext";
import cacheStorage from '../../utils/cacheStorage';

const NotificationContext = createContext(undefined);

export function useNotifications() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error("useNotifications must be used within NotificationProvider");
  }
  return context;
}

function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch {
    return null;
  }
}

export function CourierApp() {
  const [currentScreen, setCurrentScreen] = useState("dashboard");
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [hasUnread, setHasUnread] = useState(true);
  const [courierProfile, setCourierProfile] = useState({ name: null, phone: null });
  const navigate = useNavigate();

  const navigateTo = (screen, packageId) => {
    if (packageId) {
      setSelectedPackageId(packageId);
    }
    setCurrentScreen(screen);
  };

  let courierId = null;
  try {
    const token = cacheStorage.getItem("access_token");
    if (token) {
      const payload = parseJwt(token);
      courierId =
          payload["https://SmartCargo/courier_id"] ||
          payload.sub?.replace(/\D/g, "") ||
          null;
    }
  } catch (e) {
    console.error("Could not parse courier ID from token", e);
  }

  useEffect(() => {
    // Step 1: get name immediately from the id_token (no API call needed)
    const idToken = cacheStorage.getItem("id_token");
    if (idToken) {
      const payload = parseJwt(idToken);
      if (payload) {
        const nameFromToken =
            payload.name ||
            payload.nickname ||
            [payload.given_name, payload.family_name].filter(Boolean).join(" ") ||
            null;
        if (nameFromToken) {
          setCourierProfile(prev => ({ ...prev, name: nameFromToken }));
        }
      }
    }

    // Step 2: fetch phone (and authoritative name) from the backend
    const accessToken = cacheStorage.getItem("access_token");
    if (!accessToken) return;
    fetch("/api/courier/me", {
      headers: { Authorization: `Bearer ${accessToken}` }
    })
        .then(r => {
          if (!r.ok) throw new Error(`/api/courier/me returned ${r.status}`);
          return r.json();
        })
        .then(data => {
          setCourierProfile(prev => {
            const resolvedName = data.name || prev.name;
            if (resolvedName) {
              cacheStorage.setItem('user_name', resolvedName);
            }
            return { name: resolvedName, phone: data.phone || "" };
          });
        })
        .catch(err => console.warn("Could not fetch courier profile from API:", err));
  }, []);

  const handleLogout = () => {
    cacheStorage.removeItem("access_token");
    cacheStorage.removeItem("id_token");
    cacheStorage.removeItem("user_name");
    cacheStorage.removeItem("user_picture");
    cacheStorage.removeItem("user_phone");

    navigate("/login", { replace: true });
  };

  return (
      <CourierLanguageProvider courierName={courierProfile.name} courierPhone={courierProfile.phone}>
        <NotificationContext.Provider value={{ hasUnread, setHasUnread }}>
          {currentScreen === "dashboard" && (
              <CourierDashboard navigateTo={navigateTo} />
          )}
          {currentScreen === "route" && <ActiveRoute navigateTo={navigateTo} />}
          {currentScreen === "packages" && (
              <PackageList navigateTo={navigateTo} />
          )}
          {currentScreen === "package-detail" && (
              <PackageDetail packageId={selectedPackageId} navigateTo={navigateTo} />
          )}
          {currentScreen === "performance" && (
              <CourierPerformance navigateTo={navigateTo} />
          )}
          {currentScreen === "profile" && (
              <CourierProfile navigateTo={navigateTo} onLogout={handleLogout} />
          )}
          {currentScreen === "notifications" && (
              <CourierNotifications navigateTo={navigateTo} />
          )}
          {currentScreen === "privacy-security" && (
              <CourierPrivacySecurity navigateTo={navigateTo} />
          )}
          {currentScreen === "help" && <CourierHelp navigateTo={navigateTo} />}
          {currentScreen === "messages" && (
              <CourierMessages courierId={courierId} navigateTo={navigateTo} />
          )}
        </NotificationContext.Provider>
      </CourierLanguageProvider>
  );
}